/**
 * @module lib/rpdm/hierarchy
 * @summary Client wrappers for W2-A and W2-B backend endpoints.
 * @description Read + write wrappers for engagement workstreams,
 *   milestones, and work-item hierarchy mutations. Also carries the
 *   Phase-2 read for the additive endpoint
 *   `GET /api/projects/:engagementId/hierarchy` (OD-W2UI-02), gated by
 *   `capabilities.assertAvailable('hierarchy')` per § 4.4 (Rev.1a).
 *
 * ## Purpose
 * - Give W2-UI hooks a single, tested seam onto W2-A / W2-B endpoints.
 * - Enforce the Idempotency-Key discipline on every write.
 * - Guarantee no silent fallback for the additive endpoint.
 *
 * ## Inputs / Outputs
 * Every wrapper accepts primitives + an optional `{ signal }` object for
 * cancellation and returns the raw backend payload. Errors are re-thrown
 * as-is; callers translate refusal codes via `refusalCodes.translate`.
 *
 * ## Invariants (Phase 1a)
 * - Side-effect free at import time. `api` is imported but no request
 *   is issued from module scope.
 * - No consumer wired in Phase 1a; wrappers exist as an unreachable
 *   infrastructure layer until Phase 2 mounts `HierarchyTree`.
 * - Every write attaches an Idempotency-Key via `withHeader`.
 * - The additive `readEngagementHierarchy` calls `assertAvailable` before
 *   its GET so a fail-closed refusal replaces any silent legacy join.
 *
 * ## Future consuming phase
 * - Phase 2: `useHierarchyTree` invokes `listWorkstreams`,
 *   `listMilestones`, and `readEngagementHierarchy`.
 * - Phase 2: `HierarchyContextMenu` invokes `reassignWorkItemHierarchy`.
 */

import { api } from "../api";
import { withHeader } from "./idempotency";
import { assertAvailable } from "./capabilities";

/**
 * @param {string} engagementId
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<any>} raw workstream list payload.
 * @invariant Read-only. Idempotent under refresh.
 */
export async function listWorkstreams(engagementId, options) {
    const resp = await api.get(
        "/projects/engagement/" + encodeURIComponent(engagementId) + "/workstreams",
        { signal: options && options.signal },
    );
    return resp.data;
}

/**
 * @param {string} engagementId
 * @param {object} payload
 * @returns {Promise<any>}
 * @invariant Write; carries Idempotency-Key.
 */
export async function createWorkstream(engagementId, payload) {
    const resp = await api.post(
        "/projects/engagement/" + encodeURIComponent(engagementId) + "/workstreams",
        payload,
        withHeader({}),
    );
    return resp.data;
}

/**
 * @param {string} workstreamId
 * @param {object} patch
 * @returns {Promise<any>}
 * @invariant Write; carries Idempotency-Key.
 */
export async function patchWorkstream(workstreamId, patch) {
    const resp = await api.patch(
        "/projects/workstream/" + encodeURIComponent(workstreamId),
        patch,
        withHeader({}),
    );
    return resp.data;
}

/**
 * @param {string} engagementId
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<any>} raw milestone list payload.
 * @invariant Read-only. Idempotent under refresh.
 */
export async function listMilestones(engagementId, options) {
    const resp = await api.get(
        "/projects/engagement/" + encodeURIComponent(engagementId) + "/milestones",
        { signal: options && options.signal },
    );
    return resp.data;
}

/**
 * @param {string} engagementId
 * @param {object} payload
 * @returns {Promise<any>}
 * @invariant Write; carries Idempotency-Key.
 */
export async function createMilestone(engagementId, payload) {
    const resp = await api.post(
        "/projects/engagement/" + encodeURIComponent(engagementId) + "/milestones",
        payload,
        withHeader({}),
    );
    return resp.data;
}

/**
 * @param {string} milestoneId
 * @param {object} patch
 * @returns {Promise<any>}
 * @invariant Write; carries Idempotency-Key.
 */
export async function patchMilestone(milestoneId, patch) {
    const resp = await api.patch(
        "/projects/milestone/" + encodeURIComponent(milestoneId),
        patch,
        withHeader({}),
    );
    return resp.data;
}

/**
 * Reassign a work-item's parent, workstream, or milestone.
 * Refusal codes: `cycle_detected`, `max_depth_exceeded`,
 * `cross_engagement_link_forbidden`.
 *
 * @param {string} workItemId
 * @param {object} hierarchyPatch - `{ parent_work_item_id?, workstream_id?, milestone_id? }`.
 * @returns {Promise<any>}
 * @invariant Write; carries Idempotency-Key. Server enforces the invariants.
 */
export async function reassignWorkItemHierarchy(workItemId, hierarchyPatch) {
    const resp = await api.patch(
        "/projects/work-item/" + encodeURIComponent(workItemId) + "/hierarchy",
        hierarchyPatch,
        withHeader({}),
    );
    return resp.data;
}

/**
 * ADDITIVE READ-ONLY endpoint (OD-W2UI-02 · FC-2).
 * Fetches the denormalised engagement hierarchy in a single round trip.
 *
 * @param {string} engagementId
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<any>}
 * @throws {{ code: 'capability_missing' }} when the backend endpoint is
 *   not yet available in this environment. Callers translate the refusal
 *   into `<FailClosedPlaceholder capabilityMissing='hierarchy'/>`.
 * @invariant NEVER silently falls back to a legacy client-side join in
 *   production or Preview (§ 4.4).
 */
export async function readEngagementHierarchy(engagementId, options) {
    await assertAvailable("hierarchy");
    const resp = await api.get(
        "/projects/" + encodeURIComponent(engagementId) + "/hierarchy",
        { signal: options && options.signal },
    );
    return resp.data;
}
